'use client';

import React, { useEffect, Suspense } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { getJourneyId, initPostHog, trackPageView } from '@/lib/analytics';
import { observeBookingFieldVitals } from '@/lib/bookingFieldVitals';
import { safeReferrerOrigin } from '@/lib/trackingPrivacy';
import { useLenis } from '@/hooks/useLenis';

function deferToIdle(fn: () => void, timeout = 2000) {
  if (typeof window === 'undefined') return () => {};
  if (typeof window.requestIdleCallback === 'function') {
    const handle = window.requestIdleCallback(fn, { timeout });
    return () => window.cancelIdleCallback?.(handle);
  }
  const t = window.setTimeout(fn, timeout);
  return () => window.clearTimeout(t);
}

function PageViewTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    // Personal booking links carry a bearer token. Do not initialize page
    // analytics on this route, even when an old browser briefly keeps a hash.
    if (pathname === '/book-demo/l') return;
    // Capture immediately: visitors who tap or leave before idle still count.
    trackPageView(pathname);

    // First-party view beacon (ad-blocker resistant). UTMs are individual
    // fields; avoid retaining query strings that may contain an email/phone.
    const qs = new URLSearchParams(searchParams.toString());
    const stopVitals = pathname === '/book-demo/meta' ? observeBookingFieldVitals(qs) : undefined;
    let landing = pathname;
    let anon = getJourneyId();
    try {
      landing = (sessionStorage.getItem('ig_landing') ?? pathname).split('?')[0];
      if (!sessionStorage.getItem('ig_landing')) sessionStorage.setItem('ig_landing', pathname);
      anon = localStorage.getItem('ig_anon') ?? crypto.randomUUID();
      if (!localStorage.getItem('ig_anon')) localStorage.setItem('ig_anon', anon);
    } catch { /* Storage may be disabled. The journey ID is still available. */ }
    const body = JSON.stringify({
      path: pathname,
      url: window.location.origin + pathname,
      document_referrer: safeReferrerOrigin(document.referrer),
      landing_page: landing,
      utm_source: qs.get('utm_source'),
      utm_medium: qs.get('utm_medium'),
      utm_campaign: qs.get('utm_campaign'),
      utm_term: qs.get('utm_term'),
      utm_content: qs.get('utm_content'),
      anonymous_session_id: anon,
      journey_id: getJourneyId(),
    });
    if (typeof navigator.sendBeacon === 'function') {
      try {
        if (navigator.sendBeacon('/api/track', new Blob([body], { type: 'application/json' }))) return stopVitals;
      } catch { /* Fall through to fetch. */ }
    }
    fetch('/api/track', {
      method: 'POST',
      body,
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
    }).catch(() => {});
    return stopVitals;
  }, [pathname, searchParams]);

  return null;
}

export default function ClientProviders({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  useLenis(!pathname.startsWith('/book-demo'));

  useEffect(() => {
    if (pathname === '/book-demo/l') return;
    return deferToIdle(() => initPostHog(), 2000);
  }, [pathname]);

  return (
    <>
      <Suspense fallback={null}>
        <PageViewTracker />
      </Suspense>
      {children}
    </>
  );
}
